import React from "react";
import { FileText, FileDown, Loader2, Download, Upload, Undo2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { parseReport, describeReport } from "@/lib/importHtml";
import { applyImport, hasBackup, restoreBackup } from "@/storage/import";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { buildExportModel, exportFileName, downloadBlob } from "@/storage/export";
import { buildHtmlReport } from "@/lib/exportHtml";

// Descarga de todo lo entrenado en un archivo. El modelo se arma aquí, al
// abrir el diálogo, para poder enseñar de antemano qué contiene el informe: sin
// esa cuenta, exportar es un botón a ciegas.
export function ExportDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(null);

  const model = React.useMemo(() => {
    if (!open) return null;
    try {
      return buildExportModel({ email: user?.email });
    } catch (error) {
      console.error("[export] no se pudo reunir la información:", error);
      return null;
    }
  }, [open, user]);

  const run = async (format) => {
    if (!model || busy) return;
    setBusy(format);
    try {
      if (format === "html") {
        const html = buildHtmlReport(model);
        downloadBlob(new Blob([html], { type: "text/html;charset=utf-8" }), exportFileName(model, "html"));
      } else {
        // jsPDF se descarga en este momento, no al abrir la app.
        const { buildPdfReport } = await import("@/lib/exportPdf");
        downloadBlob(await buildPdfReport(model), exportFileName(model, "pdf"));
      }
      toast.success("Informe descargado");
      onOpenChange(false);
    } catch (error) {
      console.error("[export] falló la generación:", error);
      toast.error("No se ha podido generar el informe");
    } finally {
      setBusy(null);
    }
  };

  const counts = model
    ? [
        [model.summary.cyclesCompleted, "ciclos"],
        [model.exercises.length, "ejercicios"],
        [model.summary.trainingDays, "días"],
        [model.measurements.length, "medidas"],
      ]
    : [];

  const empty = model && model.summary.totalSets === 0 && model.exercises.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-sm">
        <DialogHeader className="text-left">
          <DialogTitle className="text-foreground">Exportar mis datos</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Rutina, series, pesos, medidas y días entrenados en un solo archivo.
          </DialogDescription>
        </DialogHeader>

        {counts.length > 0 && (
          <div className="grid grid-cols-4 gap-1.5">
            {counts.map(([value, label]) => (
              <div key={label} className="rounded-xl bg-secondary/25 px-1 py-2 text-center min-w-0">
                <p className="text-sm font-bold text-foreground tabular-nums leading-none">{value}</p>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground mt-1 truncate">
                  {label}
                </p>
              </div>
            ))}
          </div>
        )}

        {empty && (
          <p className="text-xs text-muted-foreground -mt-1">
            Todavía no has marcado ninguna serie: el informe saldrá con la rutina pero sin progreso.
          </p>
        )}

        <div className="space-y-2">
          <FormatButton
            icon={FileText}
            title="Informe HTML"
            hint="Se abre en cualquier navegador, también sin conexión. Desde ahí puedes imprimirlo o guardarlo en PDF."
            busy={busy === "html"}
            disabled={!model || !!busy}
            onClick={() => run("html")}
          />
          <FormatButton
            icon={FileDown}
            title="Informe PDF"
            hint="Listo para enviar o archivar, ya paginado."
            busy={busy === "pdf"}
            disabled={!model || !!busy}
            onClick={() => run("pdf")}
          />
        </div>

        <p className="text-[11px] text-muted-foreground/70 leading-snug">
          Los archivos se generan en tu dispositivo. Las imágenes de los ejercicios no se incluyen.
        </p>

        <RestoreSection onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

// El informe no es solo para mirarlo: es la copia de seguridad, y por eso la app
// sabe leerlo de vuelta. Recuperar una cuenta no debería exigir que nadie abra
// la consola del navegador.
function RestoreSection({ onDone }) {
  const inputRef = React.useRef(null);
  const [datos, setDatos] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [puedeDeshacer, setPuedeDeshacer] = React.useState(() => hasBackup());

  const elegir = async (event) => {
    const archivo = event.target.files?.[0];
    event.target.value = "";
    if (!archivo) return;

    setError(null);
    setBusy(true);
    try {
      const leido = parseReport(await archivo.text());
      setDatos(leido);
    } catch (e) {
      console.error("[import] no se pudo leer el informe:", e);
      setError(e.message || "No se ha podido leer el archivo.");
      setDatos(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmar = () => {
    try {
      const { imagenesConservadas } = applyImport(datos);
      setPuedeDeshacer(true);
      setDatos(null);
      toast.success(
        imagenesConservadas > 0
          ? `Datos restaurados, con ${imagenesConservadas} imágenes conservadas. Cierra y vuelve a abrir la app.`
          : "Datos restaurados. Cierra y vuelve a abrir la app."
      );
      onDone();
    } catch (e) {
      console.error("[import] no se pudo restaurar:", e);
      toast.error("No se han podido restaurar los datos");
    }
  };

  const deshacer = () => {
    const devueltas = restoreBackup();
    setPuedeDeshacer(false);
    toast.success(devueltas > 0 ? "Se ha vuelto a lo anterior" : "No había nada que devolver");
    onDone();
  };

  return (
    <div className="pt-3 border-t border-border/40 space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept=".html,text/html"
        onChange={elegir}
        className="hidden"
      />

      {!datos && (
        <>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-full text-left rounded-2xl glass-surface px-3.5 py-3 flex items-start gap-3 transition-colors duration-200 hover:bg-secondary/40 disabled:opacity-50"
          >
            <span
              className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: "rgba(10,133,255,0.12)", border: "1px solid rgba(10,133,255,0.22)" }}
            >
              {busy ? <Loader2 className="h-4 w-4 text-primary animate-spin" /> : <Upload className="h-4 w-4 text-primary" />}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground">Restaurar desde un informe</span>
              <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">
                Recupera tu rutina, tus series y tus medidas desde un informe HTML que hayas exportado antes.
              </span>
            </span>
          </button>

          {puedeDeshacer && (
            <button
              onClick={deshacer}
              className="w-full text-left rounded-2xl px-3.5 py-2.5 flex items-center gap-2.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Undo2 className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="text-[11px]">Deshacer la última restauración</span>
            </button>
          )}
        </>
      )}

      {error && (
        <p className="text-[11px] text-destructive flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
          {error}
        </p>
      )}

      {datos && (
        <div className="rounded-2xl glass-surface p-3.5 space-y-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Se ha encontrado esto</p>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {describeReport(datos).map(([etiqueta, valor]) => (
              <div key={etiqueta} className="flex items-baseline justify-between gap-2 min-w-0">
                <span className="text-[10px] text-muted-foreground truncate">{etiqueta}</span>
                <span className="text-[11px] font-bold text-foreground tabular-nums flex-shrink-0">{valor}</span>
              </div>
            ))}
          </div>

          {datos.avisos.length > 0 && (
            <div className="space-y-1">
              {datos.avisos.slice(0, 3).map((aviso) => (
                <p key={aviso} className="text-[10px] text-muted-foreground/80 leading-snug">· {aviso}</p>
              ))}
            </div>
          )}

          <p className="text-[10px] text-muted-foreground leading-snug">
            Esto sustituirá lo que haya ahora en tu cuenta. Se guarda antes una copia de lo actual, y puedes
            deshacerlo desde aquí mismo. Las imágenes de los ejercicios no vuelven.
          </p>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setDatos(null)}
              className="flex-1 h-10 rounded-xl glass-surface text-foreground/80 hover:text-foreground hover:bg-secondary/60 text-xs"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmar}
              className="flex-1 h-10 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold"
            >
              Restaurar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FormatButton({ icon: Icon, title, hint, busy, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left rounded-2xl glass-surface px-3.5 py-3 flex items-start gap-3 transition-colors duration-200 hover:bg-secondary/40 disabled:opacity-50 disabled:pointer-events-none"
    >
      <span
        className="h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: "rgba(10,133,255,0.12)", border: "1px solid rgba(10,133,255,0.22)" }}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        ) : (
          <Icon className="h-4 w-4 text-primary" />
        )}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">{hint}</span>
      </span>
      <Download className="h-3.5 w-3.5 text-white/20 flex-shrink-0 mt-2.5 ml-auto" />
    </button>
  );
}
